import React, { useState, useCallback, useRef, useEffect } from 'react';
import { IsomPart, DrawingElement } from '@/app/types/cad';
import { Upload, Search, Package, Trash2, Download, FolderOpen } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { ScrollArea } from '@/app/components/ui/scroll-area';
import { toast } from 'sonner';
import { useLanguage } from '@/app/contexts/LanguageContext';

interface BlockLibraryPanelProps {
  onBlockSelect: (block: IsomPart) => void;
}

export function BlockLibraryPanel({ onBlockSelect }: BlockLibraryPanelProps) {
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [library, setLibrary] = useState<IsomPart[]>([]);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const libraryInputRef = React.useRef<HTMLInputElement>(null);

  // Load library from localStorage on mount
  useEffect(() => {
    const loadLibrary = () => {
      try {
        const stored = localStorage.getItem('isompart-library');
        if (stored) {
          const parsed = JSON.parse(stored);
          setLibrary(parsed);
        }
      } catch (error) {
        console.error('❌ Error loading library from localStorage:', error);
      }
    };

    // Load on mount
    loadLibrary();

    // Listen for library updates from other components
    const handleLibraryUpdate = () => {
      loadLibrary();
    };

    window.addEventListener('isompart-library-updated', handleLibraryUpdate);
    return () => window.removeEventListener('isompart-library-updated', handleLibraryUpdate);
  }, []);

  // Save library to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('isompart-library', JSON.stringify(library));
    } catch (error) {
      console.error('❌ Error saving library to localStorage:', error);
    }
  }, [library]);

  const handleFileImport = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    console.log('📁 IMPORT:', files.length, 'file(s)');

    Array.from(files).forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const jsonString = event.target?.result as string;
          const isomPart: IsomPart = JSON.parse(jsonString);
          console.log('✅ IMPORTED:', isomPart.name, '-', isomPart.elements?.length || 0, 'elements');
          
          setLibrary(prev => [...prev, isomPart]);
          
          toast.success(t('sidebar.parts.messages.added').replace('{name}', isomPart.name));
        } catch (error) {
          console.error(' Error loading file:', file.name, error);
          toast.error(`${t('sidebar.parts.messages.importError')}: ${file.name}`);
        }
      };
      reader.readAsText(file);
    });

    // Reset input
    e.target.value = '';
  }, [t]);

  // Export entire library as .isomlib
  const handleLibraryExport = useCallback(() => {
    if (library.length === 0) {
      toast.error(t('sidebar.parts.messages.emptyLibrary'));
      return;
    }

    const filename = prompt(t('sidebar.parts.libraryExport'), 'IsomLibrary');
    if (!filename) {
      toast.info(t('sidebar.parts.messages.exportCancelled'));
      return;
    }

    const libraryData = {
      name: filename,
      version: '1.0',
      created: new Date().toISOString(),
      parts: library,
    };

    const blob = new Blob([JSON.stringify(libraryData, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename.replace(/\s+/g, '_')}.isomlib`;
    a.click();
    URL.revokeObjectURL(url);

    toast.success(t('sidebar.parts.messages.exported').replace('{count}', library.length.toString()));
  }, [library, t]);

  // Import library from .isomlib
  const handleLibraryImport = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const jsonString = event.target?.result as string;
        const libraryData = JSON.parse(jsonString);

        // Validate library structure
        if (!libraryData.parts || !Array.isArray(libraryData.parts)) {
          toast.error(t('sidebar.parts.messages.invalidFormat'));
          return;
        }

        // Check for duplicates and add new parts
        const currentLibrary = library;
        const newParts: IsomPart[] = [];
        
        libraryData.parts.forEach((part: IsomPart) => {
          const existsInLibrary = currentLibrary.some(libPart => 
            libPart.name === part.name && libPart.created === part.created
          );
          
          if (!existsInLibrary) {
            newParts.push(part);
          }
        });

        if (newParts.length > 0) {
          setLibrary(prev => [...prev, ...newParts]);
          toast.success(t('sidebar.parts.messages.imported').replace('{count}', newParts.length.toString()));
        } else {
          toast.info(t('sidebar.parts.messages.alreadyExists'));
        }
      } catch (error) {
        console.error('❌ Error loading library:', error);
        toast.error(t('sidebar.parts.messages.importError'));
      }
    };
    reader.readAsText(file);

    // Reset input
    e.target.value = '';
  }, [library, t]);

  const handleBlockDelete = useCallback((blockToDelete: IsomPart) => {
    setLibrary(prev => prev.filter(block => 
      !(block.name === blockToDelete.name && block.created === blockToDelete.created)
    ));
    toast.success(t('sidebar.parts.messages.deleted').replace('{name}', blockToDelete.name));
  }, [t]);

  const filteredLibrary = library.filter(block =>
    block.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div
      className="w-80 border-r flex flex-col"
      style={{
        backgroundColor: 'var(--md-surface-container-low)',
        borderColor: 'var(--md-outline-variant)',
      }}
    >
      {/* Header */}
      <div className="p-4 border-b" style={{ borderColor: 'var(--md-outline-variant)' }}>
        <h2 className="text-lg font-semibold mb-3">{t('sidebar.parts.title')}</h2>
        
        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            type="text"
            placeholder={t('sidebar.parts.search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8"
          />
        </div>

        {/* Import Button */}
        <Button
          onClick={() => fileInputRef.current?.click()}
          variant="outline"
          className="w-full"
        >
          <Upload className="w-4 h-4 mr-2" />
          {t('sidebar.parts.importPart')}
        </Button>
        
        <input
          ref={fileInputRef}
          type="file"
          accept=".isompart"
          multiple
          onChange={handleFileImport}
          className="hidden"
        />

        {/* Library Import/Export */}
        <div className="flex gap-2 mt-2">
          <Button
            onClick={() => libraryInputRef.current?.click()}
            variant="outline"
            className="flex-1"
          >
            <FolderOpen className="w-4 h-4 mr-2" />
            {t('sidebar.parts.libraryImport')}
          </Button>
          <Button
            onClick={handleLibraryExport}
            variant="outline"
            className="flex-1"
          >
            <Download className="w-4 h-4 mr-2" />
            {t('sidebar.parts.libraryExport')}
          </Button>
        </div>
        
        <input
          ref={libraryInputRef}
          type="file"
          accept=".isomlib"
          onChange={handleLibraryImport}
          className="hidden"
        />
      </div>

      {/* Library List */}
      <ScrollArea className="flex-1">
        <div className="p-2">
          {filteredLibrary.length === 0 ? (
            <div className="text-center py-12 px-4">
              <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-sm text-gray-500">
                {searchTerm 
                  ? t('sidebar.parts.notFound') 
                  : t('sidebar.parts.empty')}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {t('sidebar.parts.importHint')}
              </p>
            </div>
          ) : (
            filteredLibrary.map((block) => (
              <BlockLibraryItem
                key={`${block.name}-${block.created}`}
                block={block}
                onSelect={onBlockSelect}
                onDelete={handleBlockDelete}
              />
            ))
          )}
        </div>
      </ScrollArea>

      {/* Footer Info */}
      <div
        className="p-3 border-t text-xs"
        style={{
          backgroundColor: 'var(--md-surface)',
          borderColor: 'var(--md-outline-variant)',
          color: 'var(--md-on-surface-variant)',
        }}
      >
        {t('sidebar.parts.available').replace('{count}', library.length.toString())}
      </div>
    </div>
  );
}

interface BlockLibraryItemProps {
  block: IsomPart;
  onSelect: (block: IsomPart) => void;
  onDelete: (block: IsomPart) => void;
}

// Block preview thumbnail component
function BlockPreview({ block, canvasRef }: { block: IsomPart, canvasRef: React.RefObject<HTMLCanvasElement> }) {
  const angle = 30 * (Math.PI / 180);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Get elements
    const elements = block.elements || [];
    if (elements.length === 0) return;

    // Helper to convert ISO to screen (without offset/scale - raw transformation)
    const isoToScreenRaw = (u: number, v: number) => {
      const x = u * Math.cos(angle) - v * Math.cos(angle);
      const y = u * Math.sin(angle) + v * Math.sin(angle);
      return { x, y };
    };

    // STEP 1: Collect all screen-space points to find bounding box
    const screenPoints: { x: number; y: number }[] = [];

    elements.forEach(el => {
      if (el.type === 'line') {
        screenPoints.push(isoToScreenRaw(el.start.u, el.start.v));
        screenPoints.push(isoToScreenRaw(el.end.u, el.end.v));
      } else if (el.type === 'connection') {
        // Add center and approximate bounds of circle
        const center = isoToScreenRaw(el.position.u, el.position.v);
        const radiusGridUnits = (el.diameter || 50) / 100;
        // Add points around the circle to account for its size
        for (let angle = 0; angle < Math.PI * 2; angle += Math.PI / 4) {
          const offsetU = radiusGridUnits * Math.cos(angle);
          const offsetV = radiusGridUnits * Math.sin(angle);
          screenPoints.push(isoToScreenRaw(el.position.u + offsetU, el.position.v + offsetV));
        }
      } else if (el.type === 'dimension') {
        screenPoints.push(isoToScreenRaw(el.start.u, el.start.v));
        screenPoints.push(isoToScreenRaw(el.end.u, el.end.v));
      }
    });

    if (screenPoints.length === 0) return;

    // STEP 2: Calculate bounding box in screen space
    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;

    screenPoints.forEach(point => {
      minX = Math.min(minX, point.x);
      maxX = Math.max(maxX, point.x);
      minY = Math.min(minY, point.y);
      maxY = Math.max(maxY, point.y);
    });

    // Handle empty bounds
    if (!isFinite(minX) || !isFinite(maxX) || !isFinite(minY) || !isFinite(maxY)) {
      return;
    }

    // STEP 3: Calculate dimensions and center in screen space
    const width = maxX - minX;
    const height = maxY - minY;
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;

    // STEP 4: Calculate scale with padding
    const paddingFactor = 1.3; // 30% padding
    const scaleX = (canvas.width / (width || 1)) / paddingFactor;
    const scaleY = (canvas.height / (height || 1)) / paddingFactor;
    const scale = Math.min(scaleX, scaleY);

    // STEP 5: Final transformation function (with centering and scaling)
    const isoToScreen = (u: number, v: number) => {
      const raw = isoToScreenRaw(u, v);
      // Translate to center and scale
      const x = canvas.width / 2 + (raw.x - centerX) * scale;
      const y = canvas.height / 2 + (raw.y - centerY) * scale;
      return { x, y };
    };

    // STEP 6: Render all elements
    elements.forEach(el => {
      if (el.type === 'line') {
        const start = isoToScreen(el.start.u, el.start.v);
        const end = isoToScreen(el.end.u, el.end.v);

        ctx.strokeStyle = el.color || '#000000';
        ctx.lineWidth = Math.max(1, (el.width || 2) * scale * 0.01);
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        ctx.beginPath();
        ctx.moveTo(start.x, start.y);
        ctx.lineTo(end.x, end.y);
        ctx.stroke();
      } else if (el.type === 'connection') {
        const pos = isoToScreen(el.position.u, el.position.v);
        const diameterMm = el.diameter || 50;
        const radiusPixels = (diameterMm / 100) * scale / 2;

        ctx.strokeStyle = el.color || '#000000';
        ctx.lineWidth = Math.max(1, (el.strokeWidth || 5) * scale * 0.01);
        ctx.fillStyle = el.fillType === 'filled' ? (el.color || '#000000') : '#ffffff';

        ctx.beginPath();
        ctx.arc(pos.x, pos.y, radiusPixels, 0, 2 * Math.PI);
        ctx.fill();
        ctx.stroke();
      }
    });

  }, [block]);

  return (
    <canvas 
      ref={canvasRef} 
      width={96} 
      height={96}
      className="rounded"
      style={{ backgroundColor: '#ffffff' }}
    />
  );
}

function BlockLibraryItem({ block, onSelect, onDelete }: BlockLibraryItemProps) {
  const { t } = useLanguage();
  const [isDragging, setIsDragging] = useState(false);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  const handleDragStart = useCallback((e: React.DragEvent) => {
    setIsDragging(true);
    
    // Store block data in drag event
    e.dataTransfer.setData('application/json', JSON.stringify(block));
    e.dataTransfer.effectAllowed = 'copy';

    // Create custom drag image showing only the block preview
    if (previewCanvasRef.current) {
      // Clone the canvas to use as drag image
      const dragCanvas = document.createElement('canvas');
      dragCanvas.width = 96;
      dragCanvas.height = 96;
      const dragCtx = dragCanvas.getContext('2d');
      
      if (dragCtx && previewCanvasRef.current) {
        // Copy the preview canvas content
        dragCtx.drawImage(previewCanvasRef.current, 0, 0);
        
        // Add a subtle border
        dragCtx.strokeStyle = '#3b82f6';
        dragCtx.lineWidth = 2;
        dragCtx.strokeRect(0, 0, 96, 96);
        
        // Set as drag image, centered at cursor
        e.dataTransfer.setDragImage(dragCanvas, 48, 48);
      }
    }
  }, [block]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleDelete = useCallback((e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering onSelect
    onDelete(block);
  }, [block, onDelete]);

  // Count connection points
  const connectionCount = block.elements?.filter(el => el.type === 'connection').length || 0;

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onClick={() => onSelect(block)}
      className={`
        relative group p-3 mb-2 rounded border cursor-move transition-all
        hover:shadow-md hover:scale-[1.02]
        ${isDragging ? 'opacity-50 scale-95' : ''}
      `}
      style={{
        backgroundColor: 'var(--md-surface)',
        borderColor: 'var(--md-outline-variant)',
      }}
    >
      {/* Delete button - visible on hover */}
      <button
        onClick={handleDelete}
        className="absolute top-2 right-2 p-1.5 rounded opacity-0 group-hover:opacity-100 transition-opacity z-10"
        style={{
          backgroundColor: 'var(--md-error-container)',
          color: 'var(--md-on-error-container)',
        }}
        title={t('sidebar.parts.delete')}
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>

      <div className="flex items-start gap-3">
        {/* Thumbnail with preview */}
        <div className="w-24 h-24 rounded border flex-shrink-0" style={{ borderColor: 'var(--md-outline-variant)' }}>
          <BlockPreview block={block} canvasRef={previewCanvasRef} />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-sm truncate pr-8">{block.name}</h3>
          <div className="text-xs mt-1" style={{ color: 'var(--md-on-surface-variant)' }}>
            <div>{t('sidebar.parts.info.elements').replace('{count}', (block.elements?.length || 0).toString())}</div>
            {connectionCount > 0 && (
              <div>{t('sidebar.parts.info.sockets').replace('{count}', connectionCount.toString()).replace('{suffix}', connectionCount !== 1 ? 's' : '')}</div>
            )}
            {block.attributes && block.attributes.length > 0 && (
              <div>{t('sidebar.parts.info.attributes').replace('{count}', block.attributes.length.toString()).replace('{suffix}', block.attributes.length !== 1 ? 'e' : '')}</div>
            )}
          </div>
        </div>
      </div>

      {/* Drag hint */}
      <div className="text-xs mt-2 opacity-60" style={{ color: 'var(--md-primary)' }}>
        {t('sidebar.parts.dragToPlace')}
      </div>
    </div>
  );
}